import React, { Component } from "react";
import "./MusicTrack.scss";
import { observer } from "mobx-react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { reaction } from "mobx";
import { Trans } from "react-i18next";
import playSvg from "./icons/play-solid.svg?raw";
import pauseSvg from "./icons/pause-solid.svg?raw";
import downloadSvg from "./icons/download-solid.svg?raw";
import favoriteSvg from "./icons/heart-solid.svg?raw";
import similarSvg from "./icons/similar.svg?raw";
import addToPlaylistSvg from "./icons/add-to-playlist.svg?raw";
import listSvg from "./icons/list.svg?raw";
import MusicTrackState from "./MusicTrackState";
import dragPercentage from "../../utils/dragPercentage";
import AddToPlaylistMenu from "./AddToPlaylistMenu/AddToPlaylistMenu";
import WaveformDisplay from "./WaveformDisplay/WaveformDisplay";
import Timeline from "../../Timeline";

/** @typedef {import("../../../sql/schemaDatatypes").SavedPlaylistSql} SavedPlaylistSql */

/**
 * @typedef {Object} MusicTrackProps
 * @property {number} depth - Nesting depth of the track
 * @property {MusicTrackState} store - The music track state store
 * @property {number} [dragIndex] - Index for drag operations
 * @property {any} [style] - Inline styles for the component
 * @property {any} [positioningStyle] - Positioning styles
 * @property {boolean} [dontSetRefs] - Whether to skip setting refs
 * @property {boolean} [adminView] - Whether in admin view mode
 * @property {any} [t] - Translation function
 * @property {any} [i18n] - Internationalization object
 */

@observer
class MusicTrack extends Component {
  /** @type {() => void} */
  disposeReactions = () => {};

  componentDidMount() {
    const disposeVisibleChildrenResizeReaction = reaction(
      () => this.props.store.getVisibleChildren(),
      () => {
        // All our animations are 500ms, so give it 1000ms to be on the safe side
        this.props.store.measureResizeAnimation(1000);
      }
    );

    const disposeAlternateToggleResizeReaction = reaction(
      () => this.props.store.showingAlternateVersionsToggle(),
      () => {
        // All our animations are 500ms, so give it 1000ms to be on the safe side
        this.props.store.measureResizeAnimation(1000);
      }
    );

    this.disposeReactions = () => {
      disposeVisibleChildrenResizeReaction();
      disposeAlternateToggleResizeReaction();
    };
  }

  componentWillUnmount() {
    this.props.store.hideAddToPlaylistMenu();
    this.disposeReactions();
  }

  /**
   * @action
   */
  disposeReactions = () => {
    window.removeEventListener("mousemove", mouseMoveListener);
    window.removeEventListener("mouseup", mouseUpListener);
  };

  onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.removeEventListener("mousedown", this.onMouseDown);
    this.props.store.removeMenus();
  };

  render() {
    const { t, i18n } = this.props;
    let playTip = null;
    if (
      this.props.store.searchState.showPlaybackTip &&
      this.props.store.searchState.isTrackSelected(this.props.store)
    ) {
      playTip = (
        <div className={"Music-play-tip"}>
          <Trans>faster playback</Trans>
        </div>
      );
    }

    let playPause = null;
    if (this.props.store.showLoadingBar()) {
      playPause = (
        <div className={"Music-play"}>
          {playTip}
          <span
            className={"Music-button"}
            dangerouslySetInnerHTML={{ __html: pauseSvg }}
          />
        </div>
      );
    } else if (
      this.props.store.searchState.timeline.playing &&
      this.props.store.isGloballyActive()
    ) {
      playPause = (
        <div className={"Music-play"}>
          {playTip}
          <span
            className={"Music-button"}
            onMouseDown={(e) => {
              if (this.props.store.isTouch) return;
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.timeline.pause();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.timeline.pause();
            }}
            dangerouslySetInnerHTML={{ __html: pauseSvg }}
          />
        </div>
      );
    } else {
      playPause = (
        <div className={"Music-play"}>
          {playTip}
          <span
            className={"Music-button"}
            onMouseDown={(e) => {
              if (this.props.store.isTouch) return;
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.onClickedPlay();
              this.props.store.play();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.onClickedPlay();
              this.props.store.play();
            }}
            dangerouslySetInnerHTML={{ __html: playSvg }}
          />
        </div>
      );
    }

    // Render the waveform and progress bar

    let waveformHolder = null;
    let positionRendered = <div className={"Music-progress-text"} />;
    if (this.props.store.hasPlayed) {
      // If we've actually loaded the track into the browser, show the progress indicator

      if (this.props.store.isLoaded() && this.props.store.isGloballyActive()) {
        /** @type {Timeline} */
        const timeline = this.props.store.searchState.timeline;

        const percentage = timeline.trackPercentage;
        const positionIndicator = (
          <div
            className={"Music-progress-bar"}
            style={{ left: `${percentage * 100}%` }}
          />
        );

        let leftWindowIndicator = null;
        let rightWindowIndicator = null;
        if (this.props.store.searchState.showingVideoSync) {
          const videoSyncState = this.props.store.searchState.videoSyncState;
          if (
            videoSyncState.videoLoaded &&
            this.props.store.audioBuffer != null
          ) {
            const offsetPercentage =
              timeline.audioOffset / timeline.trackDuration;
            const duration = timeline.videoDuration / timeline.trackDuration;

            /*
            const onMouseDown = dragPercentage(
              newMusicOffsetPercentage => {
                timeline.setAudioOffset(
                  (newMusicOffsetPercentage * timeline.videoDuration)
                );
              },
              'Music-waveform-holder Music-waveform-holder-interactive',
              -offsetPercentage
            );
            */
            const onMouseDown = () => {};

            leftWindowIndicator = (
              <div
                className={"Music-video-window-left"}
                onMouseDown={onMouseDown}
                style={{
                  left: 0,
                  width: `${Math.min(
                    Math.max(-offsetPercentage * 100, 0),
                    100
                  )}%`
                }}
              />
            );
            rightWindowIndicator = (
              <div
                className={"Music-video-window-right"}
                onMouseDown={onMouseDown}
                style={{
                  right: 0,
                  width: `${Math.min(
                    Math.max((1 - duration + offsetPercentage) * 100, 0),
                    100
                  )}%`
                }}
              />
            );
          }
        }
        positionRendered = (
          <div className={"Music-progress-text"}>
            {timeline.renderTrackPositionText}
          </div>
        );

        waveformHolder = (
          <div
            className={
              "Music-waveform-holder Music-waveform-holder-interactive"
            }
            onMouseDown={dragPercentage(
              timeline.seekTrackPercentage,
              "Music-waveform-holder Music-waveform-holder-interactive"
            )}
          >
            {positionIndicator}
            {leftWindowIndicator}
            {rightWindowIndicator}
            <div className={"Music-waveform no-select"}>
              <WaveformDisplay
                audioBuffer={this.props.store.audioBuffer}
                renderWidth={734}
                trackId={this.props.store.music.id}
                cache={this.props.store.waveformDisplayCache}
              />
            </div>
            {/*
            <img
              className={'Music-waveform'}
              src={`https://search.ascheandspencer.com/assets/waveforms/${this.props.store.music.waveforms}.png?${this.props.store.cacheBuster}`}
            />
            */}
          </div>
        );
      } else {
        // Otherwise only show the non-interactive waveform

        waveformHolder = (
          <div
            className={
              "Music-waveform-holder Music-waveform-holder-interactive"
            }
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.play(true);
            }}
          >
            <div className={"Music-waveform no-select"}>
              <WaveformDisplay
                audioBuffer={this.props.store.audioBuffer}
                renderWidth={734}
                trackId={this.props.store.music.id}
                cache={this.props.store.waveformDisplayCache}
              />
            </div>
            {/*
            <img
              className={'Music-waveform'}
              src={`https://search.ascheandspencer.com/assets/waveforms/${this.props.store.music.waveforms}.png?${this.props.store.cacheBuster}`}
            />
            */}
          </div>
        );
      }
    }

    const isActive =
      this.props.store.isSelected() && !this.props.store.showLoadingBar();

    let loadingBar;
    if (this.props.store.showLoadingBar()) {
      loadingBar = (
        <div
          className={"Music-loading-bar"}
          style={{ width: `${this.props.store.loadingAudioProgress * 100}%` }}
        />
      );
    }

    const visibleChildren = this.props.adminView
      ? []
      : this.props.store.getVisibleChildren();
    const showAlternateVersionsToggle =
      this.props.store.showingAlternateVersionsToggle() &&
      this.props.depth === 0 &&
      !this.props.adminView;

    const listIcon = null;
    let listTip = null;
    if (this.props.store.isGloballyActive() && this.props.store.hasAnyTags()) {
      // listIcon = (
      //   <div
      //     className={"Music-list-button"}
      //     onMouseDown={(e: MouseEvent) => {
      //       e.preventDefault();
      //       e.stopPropagation();
      //       this.props.store.searchState.showRelevantTags(this.props.store);
      //     }}
      //     dangerouslySetInnerHTML={{ __html: listSvg }}
      //   />
      // );

      if (
        this.props.store.searchState.showListTip &&
        this.props.store.searchState.isTrackSelected(this.props.store)
      ) {
        listTip = (
          <div className={"Music-play-tip List-tip"}>
            Click this little box to find more tracks like this one
            <div
              className={"Music-play-tip-background"}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.searchState.hideListTip();
              }}
            />
          </div>
        );
      }
    }

    const minutes = Math.floor(this.props.store.music.duration / 60);
    const seconds = this.props.store.music.duration - minutes * 60;
    let duration;
    if (seconds < 10) duration = `${minutes}:0${seconds}`;
    else duration = `${minutes}:${seconds}`;

    let addToPlaylist = null;
    if (!this.props.adminView) {
      let submenu = null;
      if (this.props.store.showingAddToPlaylistMenu) {
        submenu = (
          <AddToPlaylistMenu
            onClose={this.props.store.hideAddToPlaylistMenu}
            addToNewSearch={() => {
              this.props.store.hideAddToPlaylistMenu();
              this.props.store.createNewSearch();
            }}
            addToExistingSearch={(search) => {
              this.props.store.hideAddToPlaylistMenu();
              this.props.store.addToSearch(search);
            }}
            removeFromExistingPlaylist={(track) => {
              const removed = this.props.store.searchState.removeFromCurrentPlaylist(
                track
              );
              console.log(removed);
            }}
            store={this.props.store.searchState}
            track={this.props.store}
            existingSearches={this.props.store.searchState.myAccountState.savedTracksOrganized()}
            isLastTrack={this.props.isLastTrack}
          />
        );
      }

      addToPlaylist = (
        <div className={"Music-add-to-playlist-holder"}>
          <div className={"Music-download"}>
            <a
              className={"Music-button"}
              dangerouslySetInnerHTML={{ __html: addToPlaylistSvg }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.showAddToPlaylistMenu();
              }}
            />
          </div>
          {submenu}
        </div>
      );
    }

    let adminTools = null;
    if (
      this.props.store.searchState.isAdmin &&
      !this.props.store.isMobileWindow
    ) {
      adminTools = (
        <div className={"Music-admin-tools-holder"}>
          <div
            className={"Music-admin-tools-button"}
            onClick={() => {
              if (this.props.store.isSelected()) {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth"
                });
              }
              if (this.props.dragIndex != null) {
                this.props.store.searchState.moveTrackToTop(
                  this.props.dragIndex
                );
              }
            }}
          >
            ^
          </div>
          <div
            className={"Music-admin-tools-button"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.showAdminTrackEditor(
                this.props.store
              );
            }}
          >
            EDIT
          </div>
        </div>
      );
    }

    let replay = null;
    let favorite = null;
    let favoriteTip = null;
    let download = null;
    if (!this.props.adminView) {
      if (
        this.props.store.searchState.showFavoritesTip &&
        this.props.store.searchState.isTrackSelected(this.props.store)
      ) {
        favoriteTip = (
          <div className={"Music-play-tip Favorites-tip"}>
            <Trans>use the plus</Trans>
            <div
              className={"Music-play-tip-background"}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.searchState.hideFavoritesTip();
              }}
            />
          </div>
        );
      }

      replay = (
        <div className={"Music-replay"}>
          <span
            className={`Music-button`}
            dangerouslySetInnerHTML={{ __html: similarSvg }}
            title={`find similar tracks`}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.searchSimilarTracks(
                this.props.store
              );
            }}
          />
        </div>
      );
      favorite = (
        <div className={"Music-favorite"}>
          {favoriteTip}
          <span
            className={`Music-button${
              this.props.store.liked &&
              this.props.store.searchState.enableHearts
                ? " Music-button-liked"
                : ""
            }`}
            dangerouslySetInnerHTML={{ __html: favoriteSvg }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.toggleLiked();
              this.props.store.searchState.showTip("favoritestip", true);
            }}
          />
        </div>
      );
      download = (
        <div className={"Music-download"}>
          <a
            className={"Music-button"}
            dangerouslySetInnerHTML={{ __html: downloadSvg }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.download();
            }}
          />
        </div>
      );
    }

    let style;
    if (this.props.store.isDragging) {
      style = {
        ...this.props.positioningStyle,
        position: "fixed",
        top: this.props.store.dragFixedTop,
        left: this.props.store.dragFixedLeft,
        pointerEvents: "none"
      };
    } else style = this.props.positioningStyle;

    let playlistMenu;
    if (
      this.props.store.searchState.playlistName &&
      this.props.store.playlistMenuActive
    ) {
      playlistMenu = (
        <ul className={"mobile-dropdown playlist-actions"}>
          <li
            className={"icon share"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.setShortlinkType("playlist");
              this.props.store.searchState.toggleSharePopup();
              this.props.store.togglePlaylistMenu();
            }}
          >
            Share
          </li>
          <li
            className={"icon delete"}
            onMouseDown={(e) => {
              const removed = this.props.store.searchState.removeFromCurrentPlaylist(
                this.props.store
              );
              this.props.store.togglePlaylistMenu();
            }}
          >
            Delete
          </li>
          <li className={"Menus-blanket"} onMouseDown={this.onMouseDown} />
        </ul>
      );
    }

    let trackMenu;
    if (this.props.store.trackMenuActive) {
      trackMenu = (
        <ul className={`mobile-dropdown track-actions`}>
          <li
            className={`icon alt ${
              this.props.store.hasChildren() ? "" : "hide"
            }`}
            onMouseDown={this.props.store.toggleShowingAlternativeVersions}
          >
            Alt Versions
          </li>
          <li
            className={"icon favorites"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.toggleLiked();
              this.props.store.toggleTrackMenu();
            }}
          >
            Favorite
          </li>
          <li
            className={"icon add-light"}
            onMouseDown={(e) => {
              this.props.store.searchState.toggleShowAddToPlaylist(
                this.props.store.music.id
              );
              this.props.store.toggleTrackMenu();
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            Add to Playlist
          </li>
          <li
            className={"icon share"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.searchState.setShortlinkTrackName(
                this.props.store.music.name.toLowerCase()
              );
              this.props.store.searchState.setShortlinkType("track");
              this.props.store.searchState.toggleSharePopup();
              this.props.store.toggleTrackMenu();
            }}
          >
            Share
          </li>
          <li
            className={"icon similar"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // this.props.store.searchState.searchTopTags(this.props.store);
              this.props.store.searchState.searchSimilarTracks(
                this.props.store
              );
              this.props.store.toggleTrackMenu();
            }}
          >
            similar tracks
          </li>

          {this.props.store.searchState.playlistName ? (
            <li
              className={"icon delete"}
              onMouseDown={(e) => {
                const removed = this.props.store.searchState.removeFromCurrentPlaylist(
                  this.props.store
                );
                this.props.store.togglePlaylistMenu();
              }}
            >
              Delete
            </li>
          ) : (
            ""
          )}

          <li className={"Menus-blanket"} onMouseDown={this.onMouseDown} />
        </ul>
      );
    }

    return (
      <div
        className={"Music-outer-container"}
        style={style}
        ref={(r) => {
          if (this.props.dontSetRefs) {
            // do nothing
          } else {
            this.props.store.setOuterDivRef(r);
          }
        }}
        onMouseOver={() => {
          if (this.props.store.searchState.isMobileWindow) return false;
          if (this.props.dragIndex != null) {
            this.props.store.searchState.mouseOver(this.props.dragIndex);
          }
        }}
      >
        {adminTools}
        <div
          className={`Music ${isActive ? "Music-active" : ""} ${
            this.props.store.showIsExclusive() ? "Music-exclusive" : ""
          } ${this.props.store.isParentActive() ? "Parent-active" : ""}`}
          style={this.props.style}
        >
          <div
            className={"Music-inner-container"}
            onMouseDown={(e) => {
              if (!this.props.store.searchState.allowDrag()) return;
              e.preventDefault();
              e.stopPropagation();
              if (this.props.dragIndex != null) {
                const dragIndex = this.props.dragIndex;
                this.props.store.searchState.startDrag(dragIndex);
                this.props.store.startDragging();

                /** @type {HTMLDivElement} */ const containerDiv = (e.currentTarget);
                const startRect = containerDiv.getBoundingClientRect();
                this.props.store.setDragFixed(startRect.left, startRect.top);

                const startX = e.clientX;
                const startY = e.clientY;

                const mouseMoveListener = (e2) => {
                  e2.preventDefault();
                  e2.stopPropagation();
                  this.props.store.setDragFixed(
                    startRect.left + e2.clientX - startX,
                    startRect.top + e2.clientY - startY
                  );
                };
                const mouseUpListener = (e2) => {
                  e2.preventDefault();
                  e2.stopPropagation();
                  const moveDistance =
                    Math.abs(e2.clientX - startX) +
                    Math.abs(e2.clientY - startY);
                  if (
                    moveDistance < 10 &&
                    this.props.store.searchState.dragEnd ===
                      this.props.store.searchState.dragStart
                  ) {
                    // Treat this like a click
                    console.log("fake drag");
                    this.props.store.searchState.selectTrack(this.props.store);
                  }
                  this.props.store.searchState.dropDrag();
                  this.props.store.stopDragging();
                  window.removeEventListener("mousemove", mouseMoveListener);
                  window.removeEventListener("mouseup", mouseUpListener);
                };

                window.addEventListener("mousemove", mouseMoveListener);
                window.addEventListener("mouseup", mouseUpListener);
              }
            }}
            onClick={(e) => {
              // if (!this.props.store.searchState.allowDrag()) return;
              e.preventDefault();
              e.stopPropagation();
              // If an event makes it all the way to this handler, than it didn't trigger
              // anything else In that case, let's select this track
              this.props.store.searchState.selectTrack(this.props.store);
            }}
            ref={r => {
              if (this.props.dontSetRefs) {
                // do nothing
              } else {
                this.props.store.containerDivRef = r;
              }
            }}
          >
            {loadingBar}
            <div
              className={`Music-name-${this.props.depth}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.props.store.playing) {
                  this.props.store.pause();
                } else {
                  this.props.store.play(true);
                }
              }}
            >
              {listIcon}
              {listTip}
              {this.props.store.music.name}
            </div>
            <div className={"Music-duration"}>{duration}</div>
            {playPause}
            {favorite}
            {replay}
            {addToPlaylist}
            {download}
            <div className={"mobile-actions"}>
              <div
                className={`mobile-actions-favorite ${
                  this.props.store.liked &&
                  this.props.store.searchState.enableHearts
                    ? " active"
                    : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.props.store.toggleLiked();
                }}
              />
              <div
                className={`mobile-actions-playlist icon add ${
                  this.props.store.playlistMenuActive ? " active" : ""
                }`}
                onMouseDown={() => {
                  this.props.store.searchState.toggleShowAddToPlaylist(
                    this.props.store.music.id
                  );
                }}
              >
                {playlistMenu}
              </div>
              <div
                className={`mobile-actions-track icon dots ${
                  this.props.store.trackMenuActive ? " active" : ""
                }`}
                onMouseDown={() => {
                  this.props.store.toggleTrackMenu();
                }}
              >
                {trackMenu}
              </div>
            </div>
            <div
              className={`Music-rest ${
                this.props.store.searchState.isMobileWindow && isActive
                  ? "show"
                  : ""
              }`}
            >
              <div className={"Music-player"}>{waveformHolder}</div>
            </div>
            {positionRendered}
          </div>
          <CSSTransition
            in={showAlternateVersionsToggle}
            timeout={1000}
            classNames={"Music-show-alternate-versions"}
            mountOnEnter
            unmountOnExit
          >
            <div
              className={"Music-show-alternate-versions"}
              onClick={e => {
                if (this.props.store.isTouch) return;
                e.preventDefault();
                e.stopPropagation();
                this.props.store.toggleShowingAlternativeVersions();
              }}
              onTouchEnd={e => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.toggleShowingAlternativeVersions();
              }}
            >
              {this.props.store.showingAlternativeVersions ? (
                <Trans>hide alternate versions</Trans>
              ) : (
                <Trans>show alternate versions</Trans>
              )}
            </div>
          </CSSTransition>
        </div>
        <div
          className={`Music-alternate-versions-holder`}
          style={{
            height: `${
              this.props.store.searchState.isMobileWindow
                ? "auto"
                : `${
                  visibleChildren.length > 0
                    ? `${35 * visibleChildren.length}px`
                    : ""
                }`
            }`
          }}
        >
          <TransitionGroup component={null}>
            {visibleChildren.map(child => (
              <CSSTransition
                key={child.music.id}
                timeout={300}
                classNames={"Music-alternate-transition"}
              >
                <MusicTrack
                  store={child}
                  key={child.music.id}
                  depth={this.props.depth + 1}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </div>
    );
  }
}

export default MusicTrack;
